/**
 * ONE instance of the prioritisation state for the whole cockpit.
 *
 * ⚠️ IT IS A PROVIDER RATHER THAN A HOOK EACH, and that is not tidiness — it is the only thing that
 * makes the Sep 25 2026 split honest. The state now has THREE readers on three different tabs:
 *
 *   Company View     prices the portfolio against it
 *   Cluster View     Balance the load writes support allocations into it
 *   Planning         the effort model and the assumptions are edited there
 *
 * Each calling `useRolePrioritisation` for itself would be three copies over one localStorage key,
 * agreeing only for as long as every write round-trips through the browser store before the next
 * component mounts. It does not: a lend committed on the Cluster View reached the Company View's
 * cluster cards only after a remount re-read the store, and the remount ALSO re-reads the shared
 * roster — which drops the allocations whenever the roster and the stored team disagree. So the
 * failure was "I entered it and it is not there", with nothing on screen to say why.
 *
 * One instance above the tab strip means a sub-tab switch is a render, not a reload. It also means
 * an uncommitted effort-model edit on Planning is already in force on the Company View, which is
 * what anyone editing it expects.
 *
 * ⚠️ THE COST, stated rather than hidden: the cockpit now reads the live portfolio and the shared
 * roster once on mount whatever tab you land on, including Pipeline Radar, which uses neither. Both
 * are cached at the proxy for 60s and the cockpit opens on Role Prioritisation by default, so in
 * practice this is the same call one render earlier. If that ever stops being true, make the
 * portfolio read lazy inside the hook — do not go back to an instance per tab.
 */
import { createContext, useContext } from 'react';
import { useRolePrioritisation } from './useRolePrioritisation';

type Value = ReturnType<typeof useRolePrioritisation>;

const Ctx = createContext<Value | null>(null);

export function RolePrioritisationProvider({
  userEmail,
  children,
}: {
  userEmail?: string | null;
  children: React.ReactNode;
}) {
  const value = useRolePrioritisation(userEmail);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * ⚠️ Throws rather than falling back to its own `useRolePrioritisation`. A silent fallback would be
 * the bug this file exists to remove, one `useContext` later and invisible: the component would
 * render correct-looking figures from a second copy of the state and disagree with every other
 * reader of it.
 */
export function useRolePrioritisationState(): Value {
  const value = useContext(Ctx);
  if (!value) {
    throw new Error('useRolePrioritisationState must be used inside <RolePrioritisationProvider>');
  }
  return value;
}
